import { Spacing } from '@persona-id/design-tokens';
import { startCase } from 'lodash/fp';
import styled, { ThemeProvider } from 'styled-components';

import { DocumentGovernmentId, DocumentGovernmentIdAttributes } from 'dashboard/models/document';
import { Label, Paragraph } from 'design-system/components/Text';
import { paragraphMixins } from 'design-system/mixins/text';
import defaultTheme from 'design-system/theme';

const GovernmentIdDetailTitle = styled('div')`
  ${paragraphMixins.md}
  color: ${({ theme }) => theme.color.text.tertiary};
  margin-bottom: ${Spacing[1]};
`;
const GovernmentIdCardContainer = styled('div')`
  ${paragraphMixins.md}
  background: white;
  width: 100%;
  &:not(:last-child) {
    border-bottom: ${({ theme }) => `1px ${theme.color.border.secondary} solid`};
  }
  padding: ${Spacing[4]} ${Spacing[5]};
  color: black;
`;

const GovernmentIdSidebarContainer = styled('div')`
  flex: 1 5 15%;
  min-width: 300px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: ${({ theme }) =>
    `${theme.element.borderRadius} ${theme.element.borderRadius} 0 0`};
  z-index: 100;
`;

const GovermentIdExtractionItemsContainer = styled('div')`
  overflow: hidden;
  &:hover {
    overflow: overlay;
  }
  border-radius: ${({ theme }) =>
    `0 0 ${theme.element.borderRadius} ${theme.element.borderRadius}`};
`;

/*  We want to show the attributes in the following order
  Full Name
  Address
  ID Number
  Issue Date
  Expiration Date
  Birthdate
  Sex
  Nationality
*/
type SidebarKeys =
  | 'name'
  | 'address'
  | 'identification_number'
  | 'issue_date'
  | 'expiration_date'
  | 'birthdate'
  | 'sex'
  | 'nationality';

type SidebarAllowedGovernmentIdAttributes = Exclude<
  keyof DocumentGovernmentIdAttributes,
  'front-photos' | 'back-photos'
>;

const SIDEBAR_KEY_GOVID_ATTRIBUTES_MAP: Record<
  SidebarKeys,
  SidebarAllowedGovernmentIdAttributes[]
> = {
  name: ['name-first', 'name-middle', 'name-last', 'name-suffix'],
  address: [
    'address-street-1',
    'address-street-2',
    'address-city',
    'address-subdivision',
    'address-postal-code',
  ],
  identification_number: ['identification-number'],
  issue_date: ['issue-date'],
  expiration_date: ['expiration-date'],
  birthdate: ['birthdate'],
  sex: ['sex'],
  nationality: ['nationality'],
};

interface GovernmentIdSidebarItemCardProps {
  govIdCardKey: string;
  govIdCardValue: string;
}

const GovernmentIdSidebarItemCard = ({
  govIdCardKey,
  govIdCardValue,
}: GovernmentIdSidebarItemCardProps) => {
  return (
    <GovernmentIdCardContainer>
      <GovernmentIdDetailTitle>
        <span>{startCase(govIdCardKey)}</span>
      </GovernmentIdDetailTitle>
      <Paragraph size='md' color={govIdCardValue ? 'primary' : 'tertiary'}>
        {govIdCardValue || 'Unable to extract'}
      </Paragraph>
    </GovernmentIdCardContainer>
  );
};

interface GovernmentIdSidebarProps {
  document: DocumentGovernmentId;
}

const GovernmentIdSidebar = ({ document }: GovernmentIdSidebarProps) => {
  const documentAttributes = document.attributes;
  const sidebarFields = Object.keys(SIDEBAR_KEY_GOVID_ATTRIBUTES_MAP).reduce(
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    (result, key: SidebarKeys) => {
      result[key] = SIDEBAR_KEY_GOVID_ATTRIBUTES_MAP[key]
        .map((attr) => documentAttributes[attr])
        .filter((x) => x != null)
        .join(' ');
      return result;
    },
    {} as Record<SidebarKeys, string>,
  );
  return (
    <ThemeProvider theme={defaultTheme}>
      <GovernmentIdSidebarContainer>
        <GovernmentIdCardContainer>
          <Label size='md'>Extractions</Label>
        </GovernmentIdCardContainer>
        <GovermentIdExtractionItemsContainer>
          {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
          {Object.keys(sidebarFields).map((govIdCardKey: SidebarKeys) => {
            return (
              <GovernmentIdSidebarItemCard
                key={`${document.id}-govid-sidebar-${govIdCardKey}`}
                govIdCardKey={govIdCardKey}
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                govIdCardValue={sidebarFields[govIdCardKey]}
              />
            );
          })}
        </GovermentIdExtractionItemsContainer>
      </GovernmentIdSidebarContainer>
    </ThemeProvider>
  );
};

export default GovernmentIdSidebar;
